import { SelectOption } from '@ardoq/select';
import { uniqBy } from 'lodash';
import {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
  SuggestionsFetchFn,
} from '../../types';
import {
  AsyncFilterType,
  QueryBuilderFilterDefinition,
  QueryBuilderFilterType,
} from '../../filterUtils';
import { IconName } from '@ardoq/icons';
import { Operator, QueryBuilderRuleValue } from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

// Those filter types are for 'Reference' - by reference OID filters.
// They can be added only on visualizations, not in the Perspective Editor.
const IGNORED_FILTER_TYPES = new Set([
  QueryBuilderFilterType.READONLY,
  QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID,
]);

type FilterGroupsWithOptionsData = {
  label: string;
  filters: Pick<
    QueryBuilderFilterDefinition,
    'filterType' | 'label' | 'id' | 'isDisabled' | 'popoverContent'
  >[];
}[];

const isFilterTypeSelectable = (
  filterType: QueryBuilderFilterDefinition['filterType']
) => !IGNORED_FILTER_TYPES.has(filterType);

export const getSelectFieldOptions = (
  filterGroups: FilterGroupsWithOptionsData
) =>
  filterGroups.map(({ label: filterGroupLabel, filters }) => {
    return {
      label: filterGroupLabel,
      options: filters
        .filter(filter => isFilterTypeSelectable(filter.filterType))
        .map(({ label: filterLabel, id, isDisabled, popoverContent }) => ({
          label: filterLabel,
          value: id,
          ...(isDisabled && { isDisabled }),
          ...(popoverContent && { popoverContent }),
        })),
    };
  });

export const isFieldDisabled = (
  filterGroups: FilterGroupsWithOptionsData,
  fieldId?: string
): boolean =>
  fieldId
    ? filterGroups.some(({ filters }) =>
        filters.some(({ id, isDisabled }) => id === fieldId && isDisabled)
      )
    : false;

export const getDefaultOperator = (filter?: QueryBuilderFilterDefinition) =>
  filter?.operators[0];

export const getDefaultValue = (filter?: QueryBuilderFilterDefinition) => {
  if (filter && filter.filterType === QueryBuilderFilterType.BOOLEAN) {
    return false;
  }
  return undefined;
};

export const maybeConvertValueForOperator = (
  currentValue?: QueryBuilderRuleValue | undefined,
  newOperator?: Operator,
  previousOperator?: Operator,
  filterType?: QueryBuilderFilterType
): { value?: QueryBuilderRuleValue | undefined } => {
  const isDateFilter =
    filterType === QueryBuilderFilterType.DATE ||
    filterType === QueryBuilderFilterType.DATE_TIME;

  if (isDateFilter) {
    // Converting to 'between' operator - needs array format
    if (
      newOperator &&
      [Operator.BETWEEN, Operator.BETWEEN_INCLUSIVE].includes(newOperator)
    ) {
      if (Array.isArray(currentValue) && currentValue.length >= 2) {
        return {
          value: [String(currentValue[0] || ''), String(currentValue[1] || '')],
        };
      }
      // Convert single string value to array format [from, to]
      const stringValue = typeof currentValue === 'string' ? currentValue : '';
      return { value: [stringValue, stringValue] };
    }

    // Converting from 'between' operator - extract first value
    if (
      previousOperator &&
      [Operator.BETWEEN, Operator.BETWEEN_INCLUSIVE].includes(
        previousOperator
      ) &&
      Array.isArray(currentValue)
    ) {
      return { value: currentValue[0] || '' };
    }
  }

  // not overwrite existing value
  return {};
};

// If we are using an ID from the suggestion then we can't skip duplicated
// labels, but if we are using a label as a value form the suggestion
// then it doesn't make sense to display the suggestion twice
const skipDuplicatedOptions =
  (suggestionsFetchFn: SuggestionsFetchFn): SuggestionsFetchFn =>
  async (searchPhrase: string) => {
    const options = await suggestionsFetchFn(searchPhrase);
    return uniqBy(options, ({ label }) => label);
  };

export const getAsyncOptionsLoaderByAsyncFilterType = (
  {
    loadComponentKeySuggestionsAsync,
    loadComponentSuggestionsAsync,
    loadParentComponentSuggestionsAsync,
    loadComponentSuggestionsIncludingCurrentComponentAsync,
    loadWorkspaceSuggestionsAsync,
    loadUserSuggestionsAsync,
    loadTagSuggestionsAsync,
    loadComponentTypeNameSuggestionsAsync,
    loadReferencedComponentTypeNameSuggestionsAsync,
    loadReferenceTypeNameSuggestionsAsync,
    loadReferenceOIDSuggestionsAsync,
  }: AsyncSuggestionsLoaders,
  asyncFilterType: AsyncFilterType
) => {
  const record: Record<
    AsyncFilterType,
    (input: string) => Promise<SelectOption<string>[]>
  > = {
    [QueryBuilderFilterType.ASYNC_COMPONENT_NAME]: skipDuplicatedOptions(
      loadComponentSuggestionsAsync
    ),
    [QueryBuilderFilterType.ASYNC_WORKSPACE_NAME]: skipDuplicatedOptions(
      loadWorkspaceSuggestionsAsync
    ),
    [QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME]: skipDuplicatedOptions(
      loadComponentTypeNameSuggestionsAsync
    ),
    [QueryBuilderFilterType.ASYNC_REFERENCED_COMPONENT_TYPE_NAME]:
      skipDuplicatedOptions(
        loadReferencedComponentTypeNameSuggestionsAsync ??
          loadComponentTypeNameSuggestionsAsync
      ),
    [QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME]: skipDuplicatedOptions(
      loadReferenceTypeNameSuggestionsAsync
    ),
    [QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE]:
      loadWorkspaceSuggestionsAsync,
    [QueryBuilderFilterType.ASYNC_COMPONENT_KEY]:
      loadComponentKeySuggestionsAsync,
    [QueryBuilderFilterType.ASYNC_COMPONENT_OID]:
      loadComponentSuggestionsIncludingCurrentComponentAsync,
    [QueryBuilderFilterType.ASYNC_SOURCE]:
      loadComponentSuggestionsIncludingCurrentComponentAsync,
    [QueryBuilderFilterType.ASYNC_TARGET]:
      loadComponentSuggestionsIncludingCurrentComponentAsync,
    [QueryBuilderFilterType.ASYNC_PARENT]: loadParentComponentSuggestionsAsync,
    [QueryBuilderFilterType.ASYNC_USER]: loadUserSuggestionsAsync,
    [QueryBuilderFilterType.ASYNC_TAGS]: loadTagSuggestionsAsync,
    [QueryBuilderFilterType.ASYNC_REFERENCE_OID]:
      loadReferenceOIDSuggestionsAsync,
    [QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID]:
      loadReferenceOIDSuggestionsAsync,
  };
  return record[asyncFilterType];
};

export const getAsyncLabelLoaderByAsyncFilterType = (
  {
    getComponentLabelAsync,
    getUserLabelAsync,
    getWorkspaceLabelAsync,
    getReferenceLabelAsync,
    getComponentKeyLabelAsync,
  }: AsyncLabelLoaders,
  asyncFilterType: AsyncFilterType
) => {
  const record: Record<
    AsyncFilterType,
    (id: string) => Promise<string | undefined>
  > = {
    [QueryBuilderFilterType.ASYNC_COMPONENT_NAME]: async (id: string) => id,
    [QueryBuilderFilterType.ASYNC_COMPONENT_KEY]: getComponentKeyLabelAsync,
    [QueryBuilderFilterType.ASYNC_SOURCE]: getComponentLabelAsync,
    [QueryBuilderFilterType.ASYNC_TARGET]: getComponentLabelAsync,
    [QueryBuilderFilterType.ASYNC_PARENT]: getComponentLabelAsync,
    [QueryBuilderFilterType.ASYNC_COMPONENT_OID]: getComponentLabelAsync,
    [QueryBuilderFilterType.ASYNC_WORKSPACE_NAME]: async (id: string) => id,
    [QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE]: getWorkspaceLabelAsync,
    [QueryBuilderFilterType.ASYNC_USER]: getUserLabelAsync,
    [QueryBuilderFilterType.ASYNC_TAGS]: async (id: string) => id,
    [QueryBuilderFilterType.ASYNC_COMPONENT_TYPE_NAME]: async (id: string) =>
      id,
    [QueryBuilderFilterType.ASYNC_REFERENCED_COMPONENT_TYPE_NAME]: async (
      id: string
    ) => id,
    [QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME]: async (id: string) =>
      id,
    [QueryBuilderFilterType.ASYNC_REFERENCE_OID]: getReferenceLabelAsync,
    [QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID]:
      getReferenceLabelAsync,
  };
  return record[asyncFilterType];
};

export const getDefaultOperatorToLabel = (
  i18nLocale: I18nLocale
): Record<Operator, string> => ({
  [Operator.BETWEEN]: t(i18nLocale, 'queryBuilder:operator.between', 'between'),
  [Operator.BETWEEN_INCLUSIVE]: t(
    i18nLocale,
    'queryBuilder:operator.between',
    'between'
  ),
  [Operator.CONTAINS]: t(
    i18nLocale,
    'queryBuilder:operator.contains',
    'contains'
  ),
  [Operator.DO_NOT_USE_CONTAINS_PHRASE]: t(
    i18nLocale,
    'queryBuilder:operator.containsPhrase',
    'contains phrase'
  ),
  [Operator.CONTAINS_SUBSTRING]: t(
    i18nLocale,
    'queryBuilder:operator.contains',
    'contains'
  ),
  [Operator.EQUAL]: t(i18nLocale, 'queryBuilder:operator.equalTo', 'equal to'),
  [Operator.GREATER]: t(
    i18nLocale,
    'queryBuilder:operator.greaterThan',
    'greater than'
  ),
  [Operator.GREATER_OR_EQUAL]: t(
    i18nLocale,
    'queryBuilder:operator.greaterThanOrEqualTo',
    'greater than or equal to'
  ),
  [Operator.IS_EMPTY]: t(
    i18nLocale,
    'queryBuilder:operator.isEmpty',
    'is empty'
  ),
  [Operator.IS_NOT_EMPTY]: t(
    i18nLocale,
    'queryBuilder:operator.isNotEmpty',
    'is not empty'
  ),
  [Operator.LESS]: t(i18nLocale, 'queryBuilder:operator.lessThan', 'less than'),
  [Operator.LESS_OR_EQUAL]: t(
    i18nLocale,
    'queryBuilder:operator.lessThanOrEqualTo',
    'less than or equal to'
  ),
  [Operator.NOT_CONTAINS_SUBSTRING]: t(
    i18nLocale,
    'queryBuilder:operator.doesNotContain',
    'does not contain'
  ),
  [Operator.NOT_CONTAINS]: t(
    i18nLocale,
    'queryBuilder:operator.doesNotContain',
    'does not contain'
  ),
  [Operator.DO_NOT_USE_NOT_CONTAINS_PHRASE]: t(
    i18nLocale,
    'queryBuilder:operator.doesNotContainPhrase',
    'does not contain phrase'
  ),
  [Operator.NOT_EQUAL]: t(
    i18nLocale,
    'queryBuilder:operator.notEqualTo',
    'not equal to'
  ),
  [Operator.IN]: t(i18nLocale, 'queryBuilder:operator.isAnyOf', 'is any of'),
  [Operator.UNSET]: t(i18nLocale, 'queryBuilder:operator.notSet', 'not set'),
  [Operator.CHECKED]: t(i18nLocale, 'queryBuilder:operator.checked', 'checked'),
  [Operator.UNCHECKED]: t(
    i18nLocale,
    'queryBuilder:operator.unchecked',
    'unchecked'
  ),
  [Operator.TO]: t(i18nLocale, 'queryBuilder:operator.to', 'to'),
  [Operator.FROM]: t(i18nLocale, 'queryBuilder:operator.from', 'from'),
});

/**
 * @deprecated Use {@link getDefaultOperatorToLabel} with an `I18nLocale` for
 *   localized operator labels. Retained for callers that cannot thread locale
 *   in scope; resolves to English defaults.
 */
export const DEFAULT_OPERATOR_TO_LABEL: Record<Operator, string> =
  getDefaultOperatorToLabel(DEFAULT_ARDOQ_I18NLOCALE);

export const getNewDefaultOperatorToLabel = (
  i18nLocale: I18nLocale
): Record<Operator, string> => ({
  ...getDefaultOperatorToLabel(i18nLocale),
  [Operator.EQUAL]: t(i18nLocale, 'queryBuilder:operator.is', 'is'),
  [Operator.NOT_EQUAL]: t(i18nLocale, 'queryBuilder:operator.isNot', 'is not'),
});

/**
 * @deprecated Use {@link getNewDefaultOperatorToLabel} with an `I18nLocale`.
 */
export const NEW_DEFAULT_OPERATOR_TO_LABEL: Record<Operator, string> =
  getNewDefaultOperatorToLabel(DEFAULT_ARDOQ_I18NLOCALE);

// DATE field type has aliases for operators
export const getDateOperatorToLabel = (
  i18nLocale: I18nLocale
): Partial<Record<Operator, string>> => ({
  [Operator.GREATER]: t(i18nLocale, 'queryBuilder:operator.after', 'after'),
  [Operator.EQUAL]: t(i18nLocale, 'queryBuilder:operator.is', 'is'),
  [Operator.LESS]: t(i18nLocale, 'queryBuilder:operator.before', 'before'),
});

/**
 * @deprecated Use {@link getDateOperatorToLabel} with an `I18nLocale`.
 */
export const DATE_OPERATOR_TO_LABEL: Partial<Record<Operator, string>> =
  getDateOperatorToLabel(DEFAULT_ARDOQ_I18NLOCALE);

export const getOperatorsIconName = (operator: Operator) => {
  if (operator === Operator.CHECKED) return IconName.CHECKBOX_CHECKED;
  if (operator === Operator.UNCHECKED) return IconName.CHECKBOX_UNCHECKED;
  if (operator === Operator.UNSET) return IconName.CHECKBOX_UNSET;
};

export const getOperatorsLabel = (
  operator: Operator,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  filterType?: QueryBuilderFilterType
): string => {
  if (
    filterType === QueryBuilderFilterType.DATE ||
    filterType === QueryBuilderFilterType.DATE_TIME
  ) {
    return (
      getDateOperatorToLabel(i18nLocale)[operator] ||
      getDefaultOperatorToLabel(i18nLocale)[operator]
    );
  }
  return getDefaultOperatorToLabel(i18nLocale)[operator];
};
